import { Link, useLocation } from "react-router-dom";
import { LogOut } from "lucide-react";
import {
  Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupLabel,
  SidebarHeader, SidebarHideWhenCollapsed, SidebarMenu, SidebarMenuButton,
  SidebarMenuItem, useSidebar,
} from "@/shared/ui/sidebar.jsx";
import { useAuth } from "@/features/auth/auth-context.jsx";
import { initials } from "@/shared/lib/format.js";
import { CLINIC_NAV, MASTER_ENTRY, isActiveRoute } from "./navigation.js";
import { isPlatformRole } from "@/shared/lib/roles.js";
import logo from "@/assets/odontoplug-logo.png";
import plugMark from "@/assets/plug-mark.png";

/**
 * Menu lateral do sistema da clínica.
 *
 * O identidade do usuário vem do contexto de sessão (servidor). O app
 * antigo montava esse bloco lendo `localStorage` e, quando não achava
 * nada, exibia um usuário fictício fixo no código.
 */
export function AppSidebar() {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const { collapsed, setMobileOpen } = useSidebar();

  // Um usuário Master vê o atalho para voltar ao painel dele.
  const grupos = CLINIC_NAV.map((grupo) =>
    grupo.label === "Sistema" && isPlatformRole(user?.role ?? "")
      ? { ...grupo, items: [...grupo.items, MASTER_ENTRY] }
      : grupo,
  );

  return (
    <Sidebar>
      <SidebarHeader>
        <div className="flex items-center gap-2.5 px-1.5 py-2">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-chart-2 shadow-lg shadow-primary/20">
            <img src={plugMark} alt="" className="h-5 w-5 object-contain" />
          </div>
          {!collapsed && (
            <img
              src={logo}
              alt="OdontoPlug"
              className="h-7 w-auto object-contain dark:brightness-0 dark:invert"
            />
          )}
        </div>
      </SidebarHeader>

      <SidebarContent>
        {grupos.map((grupo) => (
          <SidebarGroup key={grupo.label}>
            <SidebarGroupLabel>{grupo.label}</SidebarGroupLabel>
            <SidebarMenu>
              {grupo.items.map((item) => (
                <SidebarMenuItem key={item.url}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActiveRoute(pathname, item.url)}
                    tooltip={item.title}
                  >
                    <Link to={item.url} onClick={() => setMobileOpen(false)}>
                      <item.icon />
                      <span className="truncate">{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        <SidebarHideWhenCollapsed className="mb-1 flex items-center gap-2 rounded-lg px-2 py-1.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sidebar-accent text-xs font-semibold text-sidebar-accent-foreground">
            {initials(user?.fullName)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium">{user?.fullName ?? "Usuário"}</p>
            {user?.clinicName && (
              <p className="truncate text-[10px] text-sidebar-foreground/50">{user.clinicName}</p>
            )}
          </div>
        </SidebarHideWhenCollapsed>

        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={logout}
              tooltip="Sair do sistema"
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <LogOut />
              <span>Sair do Sistema</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
